// HT-4.5 · 責任者の一括確認（読み取り結果の確認をまとめて進める作業画面）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, ArrowSquareOut, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/enterprise/PageHeader';

const API = process.env.REACT_APP_BACKEND_URL;
const RESP = `${API}/api/dms/responsibilities`;

const TONE = {
  exact: 'border-teal-200 bg-teal-50 text-teal-800',
  alias: 'border-teal-200 bg-teal-50 text-teal-800',
  partial: 'border-amber-200 bg-amber-50 text-amber-900',
  ambiguous: 'border-amber-200 bg-amber-50 text-amber-900',
  none: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  not_applicable: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  empty: 'border-zinc-200 bg-zinc-50 text-zinc-500',
};

const MasterMatch = ({ m, role, documentId }) => {
  if (!m || m.match_type === 'empty') return null;
  return (
    <p className="pt-0.5 text-[11px]" data-testid={`resp-master-${documentId}-${role}`}>
      <span className={`mr-1.5 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10.5px] ${TONE[m.match_type]}`}>
        名簿照合: {m.match_label_ja}
      </span>
      {m.employee && <span className="text-zinc-700">{m.employee.label_ja}</span>}
      {m.alias_applied && (
        <span className="pl-1 text-zinc-500">（承認済みの読み替え: {m.alias_applied}）</span>
      )}
      {m.candidates?.length > 0 && (
        <span className="text-zinc-700">
          候補: {m.candidates.map((c) => c.label_ja).join(' / ')}
        </span>
      )}
      <span className="pl-1 text-zinc-400">自動では確定しません</span>
    </p>
  );
};

const FieldRow = ({ documentId, f, onSaved }) => {
  const [manual, setManual] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');

  const save = async (choice, value) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/dms/documents/${documentId}/extraction/field`,
        { field: f.role, choice, value: value || null });
      toast.success(choice === 'unknown' ? `${f.label_ja}を「不明」として記録しました`
        : `${f.label_ja}を「${r.data.value}」で確定しました`);
      setDone(choice === 'unknown' ? '「不明」として記録しました（見直せるように一覧に残ります）'
        : `「${r.data.value}」で確定しました`);
      setEditing(false);
      setManual('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-zinc-200 px-2.5 py-2"
      data-testid={`resp-field-${documentId}-${f.role}`}>
      <p className="flex flex-wrap items-center gap-1.5">
        <span className="text-[12px] font-medium text-zinc-800">{f.label_ja}</span>
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10.5px] text-amber-900"
          data-testid={`resp-state-${documentId}-${f.role}`}>{f.state_label_ja}</span>
        {f.candidate
          ? <span className="text-[12px] text-zinc-800">候補: {f.candidate}</span>
          : <span className="text-[11.5px] text-zinc-500">読み取れた候補はありません</span>}
        {f.source_ja && <span className="text-[11px] text-zinc-400">取得元: {f.source_ja}</span>}
      </p>
      {done && (
        <p className="pt-0.5 text-[11px] text-teal-800"
          data-testid={`resp-done-${documentId}-${f.role}`}>記録済み: {done}</p>
      )}
      <MasterMatch m={f.master_match} role={f.role} documentId={documentId} />
      <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
        {f.candidate && (
          <Button size="sm" disabled={busy} onClick={() => save('confirm')}
            className="h-8 bg-teal-700 text-[11.5px] hover:bg-teal-800"
            data-testid={`resp-confirm-${documentId}-${f.role}`}>この候補で確認</Button>
        )}
        <Button size="sm" variant="outline" disabled={busy} className="h-8 text-[11.5px]"
          onClick={() => setEditing((v) => !v)}
          data-testid={`resp-edit-${documentId}-${f.role}`}>氏名を入力</Button>
        <Button size="sm" variant="outline" disabled={busy} className="h-8 text-[11.5px]"
          onClick={() => save('unknown')}
          data-testid={`resp-unknown-${documentId}-${f.role}`}>不明にする</Button>
      </div>
      {editing && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
          <input value={manual} onChange={(e) => setManual(e.target.value)}
            placeholder="氏名（1〜40文字）" aria-label={`${f.label_ja}の氏名`}
            className="h-9 w-52 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid={`resp-manual-${documentId}-${f.role}`} />
          <Button size="sm" disabled={busy || !manual.trim()}
            className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
            onClick={() => save('manual', manual.trim())}
            data-testid={`resp-manual-save-${documentId}-${f.role}`}>この氏名で確定</Button>
        </div>
      )}
    </div>
  );
};

export default function ResponsibilityReview() {
  const [sum, setSum] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ role: '', state: '', profile_key: '', q: '' });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 10, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [w, s] = await Promise.all([
        axios.get(`${RESP}/worklist`, { params }),
        axios.get(`${RESP}/summary`),
      ]);
      setData(w.data);
      setSum(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一覧を取得できませんでした');
    } finally { setLoading(false); }
  }, [filters, offset]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setOffset(0); setFilters((p) => ({ ...p, [k]: v })); };
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="resp-review-page">
      <PageHeader testid="resp-review-header" title="担当者の一括確認"
        description="製図者・設計者・検図者・承認者の読み取り結果を、書類ごとにまとめて確認します。社員名簿との照合は候補の提示だけで、自動では確定しません。"
        secondary={(
          <Button variant="outline" className="h-10 text-[12px]" onClick={load}
            disabled={loading} data-testid="resp-refresh">
            <ArrowsClockwise size={13} className="mr-1" />更新
          </Button>
        )} />

      <section className="rounded-lg border border-zinc-200 bg-white p-4"
        data-testid="resp-summary">
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {(sum?.rows || []).map((r) => (
            <button key={r.role} type="button"
              onClick={() => setFilter('role', filters.role === r.role ? '' : r.role)}
              aria-pressed={filters.role === r.role}
              className={`min-h-[72px] rounded-md border px-3 py-2 text-left transition-colors duration-200 ${
                filters.role === r.role ? 'border-teal-700 bg-teal-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
              data-testid={`resp-summary-${r.role}`}>
              <p className="text-[11px] text-zinc-500">{r.label_ja}</p>
              <p className="pt-0.5 text-xl font-semibold tabular-nums text-zinc-900">
                {r.open_total}<span className="pl-0.5 text-[11px] font-normal text-zinc-500">件</span>
              </p>
              <p className="text-[10.5px] text-zinc-500">
                判読不能 {r.counts.unreadable} · 候補 {r.counts.candidate} · 不明 {r.counts.unknown}
              </p>
            </button>
          ))}
        </div>
        <p className="pt-2 text-[11.5px] text-zinc-600" data-testid="resp-summary-note">
          確認が終わっていない項目 {sum?.open_total ?? 0}件 / 図面 {sum?.drawing_documents ?? 0}件 · {sum?.notice_ja}
          {' '}「不明」として記録した項目も、あとで見直せるように一覧に残ります。
        </p>
      </section>

      <section className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <select value={filters.state} aria-label="状態でしぼり込む"
            onChange={(e) => setFilter('state', e.target.value)}
            className="h-10 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="resp-filter-state">
            <option value="">すべての状態</option>
            {(data?.states || []).map((s) => (
              <option key={s.key} value={s.key}>{s.label_ja}</option>
            ))}
          </select>
          <select value={filters.profile_key} aria-label="書類の種類でしぼり込む"
            onChange={(e) => setFilter('profile_key', e.target.value)}
            className="h-10 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="resp-filter-profile">
            <option value="">すべての種類</option>
            {(data?.profiles || []).map((p) => (
              <option key={p.key} value={p.key}>{p.label_ja}</option>
            ))}
          </select>
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)}
            placeholder="図番・書類名で探す" aria-label="図番・書類名で探す"
            className="h-10 w-56 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="resp-filter-q" />
          <span className="text-[11.5px] text-zinc-500" data-testid="resp-total">
            該当 {data?.total ?? 0}件
          </span>
        </div>

        <div className="space-y-2 pt-3">
          {rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-200 px-4 py-8 text-center"
              data-testid="resp-empty">
              <p className="text-[12px] text-zinc-500">
                確認が終わっていない責任者欄はありません。
              </p>
            </div>
          ) : rows.map((row) => (
            <div key={row.document_id} className="rounded-md border border-zinc-200 p-3"
              data-testid={`resp-row-${row.document_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-zinc-900">
                    {row.drawing_number || row.title}
                    <span className="pl-2 text-[11px] font-normal text-zinc-500">
                      {row.profile_label_ja} · {row.folder_name}
                    </span>
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {row.title} · 未確認 {row.open_count}件
                    {row.read_at && ` · 読み取り ${String(row.read_at).slice(0, 10)}`}
                  </p>
                </div>
                <a href={`/documents?open=${row.document_id}`} target="_blank" rel="noreferrer"
                  className="inline-flex min-h-[32px] items-center gap-1 text-[11.5px] text-teal-700 hover:underline"
                  data-testid={`resp-open-${row.document_id}`}>
                  <ArrowSquareOut size={12} />書類を開く
                </a>
              </div>
              <div className="grid gap-1.5 pt-2 lg:grid-cols-2">
                {row.fields.filter((f) => f.open).map((f) => (
                  <FieldRow key={f.role} documentId={row.document_id} f={f} onSaved={load} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 pt-3">
          <Button variant="outline" className="h-10 text-[12px]" disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - 10))} data-testid="resp-prev">
            <CaretLeft size={12} className="mr-1" />前へ
          </Button>
          <span className="text-[11.5px] text-zinc-500" data-testid="resp-page">
            {rows.length ? `${offset + 1}〜${offset + rows.length}件目` : '—'}
          </span>
          <Button variant="outline" className="h-10 text-[12px]"
            disabled={loading || offset + rows.length >= (data?.total ?? 0)}
            onClick={() => setOffset(offset + 10)} data-testid="resp-next">
            次へ<CaretRight size={12} className="ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
}
